<script setup>
import { useI18n } from 'vue-i18n';
import {
  assoc, assocPath, isEmpty, pipe,
} from 'ramda';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { toValue } from 'vue';
import AccountFormField from '@/components/forms/AccountFormField.vue';
import useCallEndPointWith from '@/services/backend/useCallEndPointWith';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import { useCreateSecureRemotePassword, useUserStateFromEndpoint } from '@/services/authentication';
import AccountFormFieldRadio from '@/components/forms/AccountFormFieldRadio.vue';
import AccountFormFieldSelect from '@/components/forms/AccountFormFieldSelect.vue';
import {
  communicationLanguageOptions,
  communicationPreferencesOptions, countryOptions,
  genderOptions, situationOptions,
} from '@/services/formUtilities';
import { registerSpec } from '@/services/register';
import WithLoader from '@/components/WithLoader.vue';
import passwordValidator from '@/services/passwordValidator/passwordValidator';
import getValidationMessages from '@/services/passwordValidator/validationMessages';
import { useGlobalState } from '@/store/user.store.js';
import { trackEvent, updateGtagConsent } from '@/services/analytics/googleAnalytics.js';
import AccountFormFieldCheckbox from '@/components/forms/AccountFormFieldCheckbox.vue';

const { t } = useI18n();

const validationMessages = getValidationMessages();

const emit = defineEmits(['detailsConfirmed', 'back']);

const { defineField, handleSubmit } = useForm({
  validationSchema: yup.object({
    user: yup.object({
      email: yup
        .string()
        .email(t('passwordValidator.emailValid'))
        .lowercase()
        .required(t('passwordValidator.emailRequired'))
        .label(t('registerView.accountForm.labels.email')),
      confirmEmail: yup
        .string()
        .email(t('passwordValidator.emailValid'))
        .lowercase()
        .required(t('passwordValidator.emailRequired'))
        .oneOf([yup.ref('email')], t('passwordValidator.emailMatch'))
        .label(t('registerView.accountForm.labels.confirmEmail')),
      password: passwordValidator(validationMessages).label(
        t('registerView.accountForm.labels.password'),
      ),
      confirmPassword: yup
        .string()
        .required(t('passwordValidator.passwordRequired'))
        .oneOf([yup.ref('password')], t('passwordValidator.passwordMatch'))
        .label(t('registerView.accountForm.labels.confirmPassword')),
      phoneNumber: yup.string().label(t('registerView.accountForm.labels.phoneNumber')),
      gender: yup.string().required().label(t('registerView.accountForm.labels.gender')),
      firstName: yup.string().required().label(t('registerView.accountForm.labels.firstName')),
      lastName: yup.string().required().label(t('registerView.accountForm.labels.lastName')),
      communicationLanguage: yup
        .string()
        .label(t('registerView.accountForm.labels.communicationLanguage')),
      department: yup.string().label(t('registerView.accountForm.labels.department')),
      roleInCompany: yup
        .string()
        .label(t('registerView.accountForm.labels.roleInCompany')),
    }),
    company: yup
      .object({
        companyName: yup
          .string()
          .min(3)
          .required()
          .label(t('registerView.accountForm.labels.companyName')),
        numberOfEmployees: yup
          .number()
          .label(t('registerView.accountForm.labels.numberOfEmployees')),
        industry: yup.string().label(t('registerView.accountForm.labels.industry')),
        website: yup.string().label(t('registerView.accountForm.labels.website')),
        legalForm: yup.string().label(t('registerView.accountForm.labels.legalForm')),
        taxNumber: yup.string().label(t('registerView.accountForm.labels.taxNumber')),
        commercialRegisterNumber: yup
          .string()
          .label(t('registerView.accountForm.labels.commercialRegisterNumber')),
        streetAddress: yup
          .string()
          .label(t('registerView.accountForm.labels.streetAddress')),
        zipCode: yup.string().label(t('registerView.accountForm.labels.zipCode')),
        country: yup.string().required().label(t('registerView.accountForm.labels.country')),
        city: yup.string().label(t('registerView.accountForm.labels.city')),
      })
      .nullable()
      .default(null),
    termsAccepted: yup
      .boolean()
      .oneOf([true], t('registerView.accountForm.labels.termsRequired')).label(t('registerView.accountForm.labels.terms')),
    privacyAccepted: yup
      .boolean()
      .oneOf([true], t('registerView.accountForm.labels.privacyRequired')).label(t('registerView.accountForm.labels.privacy')),
  }),
});

const [email, emailAttrs] = defineField('user.email');
const [confirmEmail, confirmEmailAttrs] = defineField('user.confirmEmail');
const [password, passwordAttrs] = defineField('user.password');
const [confirmPassword, confirmPasswordAttrs] = defineField('user.confirmPassword');

const [gender, genderAttrs] = defineField('user.gender');
const [firstName, firstNameAttrs] = defineField('user.firstName');
const [lastName, lastNameAttrs] = defineField('user.lastName');
const [companyName, companyNameAttrs] = defineField('company.companyName');
const [country, countryAttrs] = defineField('company.country');
const [termsAccepted, termsAcceptedAttrs] = defineField('user.termsAccepted');
const [privacyAccepted, privacyAcceptedAttrs] = defineField('user.privacyAccepted');
/*
const [roleInCompany, roleInCompanyAttrs] = defineField('user.roleInCompany');
const [department, departmentAttrs] = defineField('user.department');
const [communicationPreference, communicationPreferenceAttrs] = defineField(
  'user.communicationPreference',
);
const [communicationLanguage, communicationLanguageAttrs] = defineField(
  'user.communicationLanguage',
);
const [phoneNumber, phoneNumberAttrs] = defineField('user.phoneNumber');
const [streetAddress, streetAddressAttrs] = defineField('company.streetAddress');
const [zipCode, zipCodeAttrs] = defineField('company.zipCode');
const [city, cityAttrs] = defineField('company.city');
const [numberOfEmployees, numberOfEmployeesAttrs] = defineField('company.numberOfEmployees');
const [industry, industryAttrs] = defineField('company.industry');
const [website, websiteAttrs] = defineField('company.website');
const [legalForm, legalFormAttrs] = defineField('company.legalForm');
const [taxNumber, taxNumberAttrs] = defineField('company.taxNumber');
const [commercialRegisterNumber, commercialRegisterNumberAttrs] = defineField(
  'company.commercialRegisterNumber',
);
*/
country.value = 'de';

const {
  callEndpoint,
  userFriendlyError,
  isLoading: isSendingRegisterForm,
} = useCallEndPointWith('register');

const { userState, currentEmployees, currentCourses } = useGlobalState();
const { getUserStateFromEndpoint, isLoading } = useUserStateFromEndpoint();

const submit = handleSubmit(async (values) => {
  try {
    const username = values.user.email;
    const { password } = values.user;

    const usernameLowercased = username.toLowerCase();

    const { salt, verifier } = useCreateSecureRemotePassword(usernameLowercased, password);

    const request = pipe(
      assocPath(['user', 'salt'], toValue(salt)),
      assocPath(['user', 'verifier'], toValue(verifier)),
      registerSpec,
    )(values);

    await callEndpoint(assoc('method', 'POST', { request }));
    updateGtagConsent({ 'analytics_storage': 'granted' });
    updateGtagConsent({ 'ad_storage': 'granted' });
    updateGtagConsent({ 'ad_user_data': 'granted' });
    updateGtagConsent({ 'ad_personalization': 'granted' });
    localStorage.setItem('gaRegisteredUserConsent', 'true');
    if (isEmpty(userFriendlyError.value)) {
      const userStateFromEndpoint = await getUserStateFromEndpoint(usernameLowercased, values.user.password);// eslint-disable-line max-len

      if (!isEmpty(userStateFromEndpoint)) {
        userState.value = assoc('isAuthenticated', true, userStateFromEndpoint);
        currentEmployees.value = userStateFromEndpoint.employees;
        currentCourses.set(userStateFromEndpoint.courses);
      }
      trackEvent('company_registration_success', {
        event_category: 'Register',
        event_label: 'New company user',
      });
      emit('detailsConfirmed', values);
    }
  } catch (error) {
    trackEvent('company_registration_exception', {
      event_category: 'Register',
      event_label: 'Error registering company User',
      error: error.message || 'unknown',
    });
    if (error.response && error.response.data && error.response.data.error) {
      userFriendlyError.value = error.response.data.error;
    } else {
      userFriendlyError.value = t('registerView.accountForm.errors.userExists');
    }
  }
});
</script>

<template>
  <div>
    <h2 class="text-3xl text-center text-basecolor mb-8">
      {{ $t('registerView.accountForm.title') }}
    </h2>

    <form
      @submit="submit"
      class="m-10"
    >
      <div class="max-w-4xl mx-auto px-16 py-8 base-gradient text-primary rounded-lg gap-x-3">
        <h2 class="text-2xl">
          {{ $t('registerView.accountForm.companyDetailsTitle') }}
        </h2>
        <div class="px-4 pb-36 pt-8 sm:px-6 lg:col-start-1 lg:row-start-1 lg:px-0 lg:pb-16">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3 items-center">
            <InPlaceAlert
              :message="userFriendlyError"
              type="error"
              class="col-span-2"
              @close-alert="userFriendlyError = ''"
            />
            <AccountFormField
              id="company.companyName"
              :label="$t('registerView.accountForm.labels.companyName')"
              :is-required="true"
              v-model="companyName"
              input-type="text"
              v-bind="companyNameAttrs"
              auto-complete-type="organization"
            />
            <AccountFormFieldSelect
              id="company.country"
              :label="$t('registerView.accountForm.labels.country')"
              :is-required="true"
              v-model="country"
              v-bind="countryAttrs"
              :options="countryOptions"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3" />
        </div>
      </div>

      <div class="max-w-4xl mx-auto px-16 py-8 mt-8 base-gradient text-primary rounded-lg">
        <h2 class="text-2xl">
          {{ $t('registerView.accountForm.ownerDetailsTitle') }}
        </h2>
        <div class="px-4 pb-36 pt-8 sm:px-6 lg:col-start-1 lg:row-start-1 lg:px-0 lg:pb-16">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <div class="grid grid-cols-2 md:grid-cols-3 gap-x-3">
              <AccountFormFieldSelect
                class="md:col-span-1"
                id="user.gender"
                :label="$t('registerView.accountForm.labels.gender')"
                :is-required="true"
                v-model="gender"
                v-bind="genderAttrs"
                auto-complete-type="gender"
                :options="genderOptions"
              />
              <AccountFormField
                class="md:col-span-2"
                id="user.firstName"
                :label="$t('registerView.accountForm.labels.firstName')"
                :is-required="true"
                v-model="firstName"
                input-type="text"
                v-bind="firstNameAttrs"
                auto-complete-type="firstname"
              />
            </div>
            <AccountFormField
              id="user.lastName"
              :label="$t('registerView.accountForm.labels.lastName')"
              :is-required="true"
              v-model="lastName"
              input-type="text"
              v-bind="lastNameAttrs"
              auto-complete-type="lastname"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <AccountFormField
              id="user.email"
              :label="$t('registerView.accountForm.labels.email')"
              :is-required="true"
              v-model="email"
              v-bind="emailAttrs"
              input-type="email"
              auto-complete-type="email"
            />
            <AccountFormField
              id="user.confirmEmail"
              :label="$t('registerView.accountForm.labels.confirmEmail')"
              :is-required="true"
              v-model="confirmEmail"
              v-bind="confirmEmailAttrs"
              input-type="email"
              auto-complete-type="confirm-email"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <AccountFormField
              id="user.password"
              :label="$t('registerView.accountForm.labels.password')"
              :is-required="true"
              v-model="password"
              v-bind="passwordAttrs"
              input-type="password"
              auto-complete-type="password"
            />
            <AccountFormField
              id="user.confirmPassword"
              :label="$t('registerView.accountForm.labels.confirmPassword')"
              :is-required="true"
              v-model="confirmPassword"
              v-bind="confirmPasswordAttrs"
              input-type="password"
              auto-complete-type="confirm-password"
            />
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
            <AccountFormFieldCheckbox
              id="termsAccepted"
              :label="$t('registerView.accountForm.labels.terms')"
              :is-required="true"
              v-model="termsAccepted"
              v-bind="termsAcceptedAttrs"
            />
            <AccountFormFieldCheckbox
              id="privacyAccepted"
              :label="$t('registerView.accountForm.labels.privacy')"
              :is-required="true"
              v-model="privacyAccepted"
              v-bind="privacyAcceptedAttrs"
            />
          </div>
        </div>
      </div>
      <div class="flex flex-row justify-end max-w-4xl mx-auto mt-8 ">
        <WithLoader :is-loading="isSendingRegisterForm || isLoading">
          <button
            type="submit"
            class="w-full mt-4 px-14 py-2 btn-cta sm:order-last sm:w-auto"
          >
            {{ $t('registerView.accountForm.confirm') }}
          </button>
        </WithLoader>
      </div>
    </form>
  </div>
</template>
